import {
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  Output,
  ViewChild,
  WritableSignal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import * as domtoimage from 'dom-to-image';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import { BaseChartDirective } from 'ng2-charts';
import { BarChartService as ChartService } from '../../services/bar-chart.service';
import { HorizontalBarsKpi } from '../../types';
import { HorizontalBarsModalComponent } from './horizontal-bars-modal/horizontal-bars-modal.component';

type AccessibleChartRow = {
  label: string;
  values: string[];
};

let nextHorizontalBarsId = 0;

@Component({
  selector: 'app-horizontal-bars',
  templateUrl: './horizontal-bars.component.html',
  providers: [ChartService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class HorizontalBarsComponent {
  @ViewChild(BaseChartDirective) chart: BaseChartDirective;

  @Input({ required: true }) set data(v: HorizontalBarsKpi) {
    this.data$.set(v);
  }

  @Input() loading: boolean = false;

  @Input() total: number = 0;

  @Output() navigate = new EventEmitter<string>();

  @ViewChild('modalContent') modalContent: ElementRef | undefined;

  // #region     BEGIN ChartService
  readonly chartService$ = inject(ChartService);

  get data$(): WritableSignal<HorizontalBarsKpi> {
    return this.chartService$.data$;
  }

  readonly updateChartEffect$ = effect(() => {
    this.chartService$.barChartData$();
    this.chart?.update();
  });
  // #endregion  END ChartService

  readonly chartDescId = `horiz-bars-card-desc-${(nextHorizontalBarsId += 1)}`;

  readonly chartTableTitleId = `horiz-bars-card-table-title-${nextHorizontalBarsId}`;

  readonly chartTitle$ = computed(() => this.data$()?.title || 'Bar chart');

  readonly accessibleChartColumns$ = computed<string[]>(() => {
    const datasets = this.chartService$.barChartData$().datasets ?? [];

    return datasets.map(
      (dataset, index) =>
        dataset.label ||
        (datasets.length === 1
          ? this.data$()?.xAxisLabel || 'Value'
          : `Series ${index + 1}`),
    );
  });

  readonly accessibleChartRows$ = computed<AccessibleChartRow[]>(() => {
    const chartData = this.chartService$.barChartData$();
    const labels = chartData.labels ?? [];
    const datasets = chartData.datasets ?? [];

    return labels.map((label, rowIndex) => ({
      label: String(label),
      values: datasets.map((dataset) => String(dataset.data?.[rowIndex] ?? '')),
    }));
  });

  readonly chartDescription$ = computed(() => {
    const title = this.chartTitle$();
    const barCount = this.accessibleChartRows$().length;
    const barLabel = barCount === 1 ? 'bar' : 'bars';

    return `${title} bar chart with ${barCount} ${barLabel}. The data table lists each label and value.`;
  });

  private readonly matDialog = inject(MatDialog);

  openModal(): void {
    this.matDialog.open<HorizontalBarsModalComponent, HorizontalBarsKpi>(
      HorizontalBarsModalComponent,
      {
        width: 'calc(100vw - 1rem)',
        data: this.data$(),
        hasBackdrop: true,
        ariaLabelledBy: 'horiz-bars-dialog-title',
        ariaDescribedBy: 'horiz-bars-chart-desc',
      },
    );
  }

  downloadImage(): void {
    const contentElement = this.modalContent?.nativeElement;

    if (contentElement) {
      const options = {
        width: contentElement.offsetWidth,
        height: contentElement.offsetHeight,
        scale: 1.5,
        style: {
          transform: 'scale(1)',
          transformOrigin: 'top center',
        },
      };

      domtoimage
        .toPng(contentElement, options)
        .then((dataUrl) => {
          const link = document.createElement('a');
          link.href = dataUrl;
          link.download = 'component-image.png';
          link.click();
        })
        .catch((error) => {
          console.warn('Error', error);
        });
    }
  }

  async downloadXlsx(): Promise<void> {
    const chartData = this.chartService$.barChartData$();

    const data = [];
    const { labels } = chartData;

    const headers = [
      'Label',
      ...chartData.datasets.map((dataset) => dataset.label),
    ];
    data.push(headers);

    labels.forEach((label, index) => {
      const row = [label];
      chartData.datasets.forEach((dataset) => {
        row.push(dataset.data[index]);
      });
      data.push(row);
    });

    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Chart Data');
    worksheet.addRows(data);
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }),
      'chart.xlsx',
    );
  }

  // Ensure chart adapts to viewport changes
  @HostListener('window:resize')
  onWindowResize(): void {
    this.chart?.update();
  }
}
